import dayjs, { Dayjs } from "dayjs";
import { ScheduleCard } from "../scheduler/types";
import { GetScheduleResponse, ScheduleCreateRequest, WarehouseSize } from "../../types";
import { WarehouseScheduleRecurrenceEndDate } from "../scheduler/constants";
import { getEventTypeFromApiResponse } from "../../WarehouseScheduler";

/**
 * Rounds a dayjs object to the nearest 15-minute interval.
 * Examples: 12:07 -> 12:00, 12:08 -> 12:15, 12:22 -> 12:15, 12:23 -> 12:30
 */
export const roundTo15Minutes = (time: Dayjs): Dayjs => {
  const minutes = time.minute();
  const roundedMinutes = Math.round(minutes / 15) * 15;
  return time
    .minute(roundedMinutes % 60)
    .second(0)
    .millisecond(0)
    .add(Math.floor(roundedMinutes / 60), "hour");
};

export interface FormValues {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  scheduleBlocker: string;
  currentWarehouseSize: string;
  warehouseChangeTo: string;
  clusterMin: number;
  clusterMax: number;
  // repeat: number;
  // repeatUnit: string;
  repeatOn: (string | undefined)[];
  ends: string;
  endsAfterOccurrences: number;
  endsOnDate: string;
}

export interface WarehouseInitialValuesProps {
  warehouseTo: string;
  warehouseFrom: string;
  clusterMin?: number;
  clusterMax?: number;
  startDate?: string;
  endDate?: string;
}

export const initialValues = ({
  warehouseTo,
  warehouseFrom,
  clusterMin,
  clusterMax,
  startDate,
  endDate,
}: WarehouseInitialValuesProps) => ({
  startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
  startTime: startDate ? roundTo15Minutes(dayjs(startDate)).format("HH:mm") : "23:00",
  endDate: endDate
    ? dayjs(endDate).format("YYYY-MM-DD")
    : dayjs().add(1, "hour").format("YYYY-MM-DD"),
  endTime: endDate ? roundTo15Minutes(dayjs(endDate)).format("HH:mm") : "00:00",
  scheduleBlocker: "No",
  currentWarehouseSize: warehouseFrom,
  warehouseChangeTo: warehouseTo,
  clusterMin: clusterMin || 1,
  clusterMax: clusterMax || 8,
  // repeat: 1,
  // repeatUnit: "Week",
  repeatOn: [],
  ends: "Never",
  endsAfterOccurrences: 10,
  endsOnDate: "",
});

export const getInitialValues = (card: ScheduleCard) => {
  return {
    startDate: dayjs(card.start).format("YYYY-MM-DD"),
    startTime: roundTo15Minutes(dayjs(card.start)).format("HH:mm"),
    endDate: dayjs(card.end).format("YYYY-MM-DD"),
    endTime: roundTo15Minutes(dayjs(card.end)).format("HH:mm"),
    scheduleBlocker: card.type === "block" ? "Yes" : "No",
    // sizeTo is selected as the current warehouse size for edit only
    currentWarehouseSize: card.sizeFrom ?? "",
    warehouseChangeTo: "",
    clusterMin: card.clusterMin || 1,
    clusterMax: card.clusterMax || 8,
    // repeat: 1,
    // repeatUnit: "Week",
    repeatOn: card.recurrencePattern?.days || [] || undefined,
    ends: card.recurrencePattern
      ? card.recurrencePattern.end_date === WarehouseScheduleRecurrenceEndDate
        ? "Never"
        : card.recurrencePattern.end_date === card.start
          ? "On"
          : "After"
      : "Never",
    endsAfterOccurrences: card.recurrencePattern
      ? dayjs(card.recurrencePattern.end_date).diff(dayjs(card.start), "days") || 10
      : 10,
    endsOnDate: card.recurrencePattern
      ? dayjs(card.recurrencePattern.end_date).format("YYYY-MM-DD")
      : "",
  };
};

export const addOrUpdateFormSubmission = (
  warehouseName: string,
  instanceId: string,
  mutate = (_data: ScheduleCreateRequest & { instance_id: string }) => {},
  values: FormValues,
  schedulerData?: GetScheduleResponse,
  scheduleId?: string
) => {
  // Parse full datetime by combining date and time values, rounding to 15-minute intervals
  const startDateTime = roundTo15Minutes(
    dayjs(values.startDate)
      .set("hour", parseInt(values.startTime.split(":")[0], 10))
      .set("minute", parseInt(values.startTime.split(":")[1], 10))
  );

  const endDateTime = roundTo15Minutes(
    dayjs(values.endDate)
      .set("hour", parseInt(values.endTime.split(":")[0], 10))
      .set("minute", parseInt(values.endTime.split(":")[1], 10))
  );

  const now = roundTo15Minutes(dayjs());

  // Adjust start time if it's in the past
  const startTime = startDateTime.isBefore(now) ? now : startDateTime;

  // Adjust end time: if original end is before adjusted start, add 1 hour from start
  const endTime =
    endDateTime.isBefore(startTime) || endDateTime.isSame(startTime)
      ? startTime.add(1, "hour")
      : endDateTime;

  // Construct the API request payload
  const data: ScheduleCreateRequest = {
    warehouse_name: warehouseName, // Replace with the dynamic warehouse name if applicable
    instance_id: instanceId,
    warehouse_configuration: {
      warehouse_size: values.warehouseChangeTo as WarehouseSize,
    },
    start_time: startTime.toISOString(),
    end_time: endTime.toISOString(),
    schedule_type: values.scheduleBlocker === "Yes" ? "block" : "manual",
    reason: "custom",
    recurrence_pattern: null,
  };

  const overlappingTime = schedulerData?.find(card => {
    // Skip comparing with itself if editing an existing schedule
    if (String(card.schedule_id) === scheduleId) return false;

    const cardStart = dayjs(card.start_time);
    const cardEnd = dayjs(card.end_time);
    return startTime.isBefore(cardEnd) && endTime.isAfter(cardStart);
  });

  if (overlappingTime) {
    throw new Error(
      `This schedule block overlaps with existing schedule block - ${getEventTypeFromApiResponse(overlappingTime.schedule_type)}: ${dayjs(overlappingTime.start_time).format("h:mm a")} - ${dayjs(overlappingTime.end_time).format("h:mm a")}. Please delete that schedule block first.`
    );
  }
  // let recurrencePattern: RecurrencePattern | null = null;

  // if (values.ends === "Never") {
  //   recurrencePattern = {
  //     days: values.repeatOn || [],
  //     end_date: WarehouseScheduleRecurrenceEndDate,
  //   };
  // } else if (values.ends === "On") {
  //   const endDate = dayjs(values.endsOnDate)
  //     .set("hour", 23)
  //     .set("minute", 59)
  //     .set("second", 59)
  //     .toISOString();
  //   recurrencePattern = {
  //     days: values.repeatOn || [],
  //     end_date: endDate,
  //   };
  // } else if (values.ends === "After") {
  //   const occurrences = values.endsAfterOccurrences;
  //   if (!isNaN(occurrences) && occurrences > 0) {
  //     const approximateEndDate = startDay
  //       .add(occurrences * (values.endsAfterOccurrences || 1), "day")
  //       .toISOString();
  //     recurrencePattern = {
  //       days: values.repeatOn || [],
  //       end_date: approximateEndDate,
  //     };
  //   }
  // }

  // if (recurrencePattern) {
  //   data.recurrence_pattern = recurrencePattern;
  // }

  mutate({ ...data, instance_id: instanceId });
};
